// src/screens/AdminDealersScreen.js
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { adminAPI } from '../services/api';
import DealershipsPanel from '../components/admin/DealershipsPanel';

const AdminDealersScreen = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [dealers, setDealers] = useState([]);
  const [loadingDealers, setLoadingDealers] = useState(true);
  const [error, setError] = useState('');

  // form state for creating a dealer user
  const [selectedDealerId, setSelectedDealerId] = useState('');
  const [dealerUserName, setDealerUserName] = useState('');
  const [dealerUserEmail, setDealerUserEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // form state for creating a kiosk user
  const [kioskDealerId, setKioskDealerId] = useState('');
  const [kioskUserName, setKioskUserName] = useState('');
  const [kioskUserEmail, setKioskUserEmail] = useState('');
  const [kioskSubmitting, setKioskSubmitting] = useState(false);
  const [kioskSuccessMessage, setKioskSuccessMessage] = useState('');

  // Only admins allowed
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  // Load every dealership (admin list: includes trial, lapsed and canceled ones)
  useEffect(() => {
    const fetchDealers = async () => {
      try {
        setLoadingDealers(true);
        setError('');
        const { data } = await adminAPI.listDealers();
        setDealers(data);
      } catch (err) {
        console.error('Error fetching dealers', err);
        setError('Failed to load dealers.');
      } finally {
        setLoadingDealers(false);
      }
    };

    if (user && user.role === 'admin') {
      fetchDealers();
    }
  }, [user]);

  const handleCreateDealerUser = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setError('');

    if (!selectedDealerId) {
      setError('Please select a dealer.');
      return;
    }

    if (!dealerUserName || !dealerUserEmail) {
      setError('Please fill in all fields for the dealer user.');
      return;
    }

    try {
      setSubmitting(true);
      await adminAPI.createDealerUser({
        name: dealerUserName,
        email: dealerUserEmail,
        dealerId: selectedDealerId,
      });

      setSuccessMessage(`Dealer user created. A set-password email was sent to ${dealerUserEmail}.`);
      // clear form
      setDealerUserName('');
      setDealerUserEmail('');
    } catch (err) {
      console.error('Error creating dealer user', err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to create dealer user.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateKioskUser = async (e) => {
    e.preventDefault();
    setKioskSuccessMessage('');
    setError('');

    if (!kioskDealerId) {
      setError('Please select a dealer for the kiosk.');
      return;
    }

    if (!kioskUserName || !kioskUserEmail) {
      setError('Please fill in all fields for the kiosk user.');
      return;
    }

    try {
      setKioskSubmitting(true);
      const response = await adminAPI.createDealerUser({
        name: kioskUserName,
        email: kioskUserEmail,
        dealerId: kioskDealerId,
        role: 'kiosk',
      });

      const kioskId = response.data?.kioskId;
      setKioskSuccessMessage(
        `Kiosk user created (Kiosk ID: ${kioskId || 'N/A'}). A set-password email was sent to ${kioskUserEmail}.`
      );
      // clear form
      setKioskUserName('');
      setKioskUserEmail('');
    } catch (err) {
      console.error('Error creating kiosk user', err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to create kiosk user.';
      setError(msg);
    } finally {
      setKioskSubmitting(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-app-border-light dark:border-app-border flex flex-col items-center text-center">
        <h1 className="text-xl font-semibold">Admin – Dealers</h1>
        <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-1">
          Manage dealerships and their accounts
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-20 space-y-4 text-xs">
        {/* Errors / success */}
        {error && (
          <div className="bg-red-900/40 text-red-300 px-3 py-2 rounded-xl text-[11px]">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="bg-green-900/30 text-green-300 px-3 py-2 rounded-xl text-[11px]">
            {successMessage}
          </div>
        )}
        {kioskSuccessMessage && (
          <div className="bg-green-900/30 text-green-300 px-3 py-2 rounded-xl text-[11px]">
            {kioskSuccessMessage}
          </div>
        )}

        {/* Dealerships: list, add, edit */}
        <DealershipsPanel
          dealers={dealers}
          loading={loadingDealers}
          onSaved={(saved) => setDealers((list) => {
            const others = list.filter((d) => d._id !== saved._id);
            return [...others, saved].sort((a, b) => a.name.localeCompare(b.name));
          })}
        />

        {/* Create dealer user form */}
        <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-3 border border-app-border-light dark:border-app-border space-y-2">
          <div className="font-semibold text-sm">
            Create dealer user account
          </div>
          <p className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary mb-1">
            Dealer users can log in, see bookings for their dealership, and
            manage test drive requests.
          </p>

          <form onSubmit={handleCreateDealerUser} className="space-y-2">
            <div>
              <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">
                Dealership
              </label>
              <select
                className="mt-1 w-full px-3 py-2 rounded-xl bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border outline-none text-[11px]"
                value={selectedDealerId}
                onChange={(e) => setSelectedDealerId(e.target.value)}
              >
                <option value="">Select a dealer</option>
                {dealers.map((d) => (
                  <option key={d._id} value={d._id}>
                    {d.name}
                    {d.address?.city && d.address?.state
                      ? ` – ${d.address.city}, ${d.address.state}`
                      : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">
                Dealer user name
              </label>
              <input
                type="text"
                className="mt-1 w-full px-3 py-2 rounded-xl bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border outline-none text-[11px]"
                value={dealerUserName}
                onChange={(e) => setDealerUserName(e.target.value)}
                placeholder="e.g. Jane Smith"
              />
            </div>

            <div>
              <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">
                Dealer user email
              </label>
              <input
                type="email"
                className="mt-1 w-full px-3 py-2 rounded-xl bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border outline-none text-[11px]"
                value={dealerUserEmail}
                onChange={(e) => setDealerUserEmail(e.target.value)}
                placeholder="e.g. jane@dealer.com"
              />
            </div>

            <p className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">
              They will receive an email with a link to set their own password.
            </p>

            <button
              type="submit"
              disabled={submitting}
              className={`mt-2 w-full py-3 rounded-2xl text-sm font-semibold ${
                submitting
                  ? 'bg-app-bg-light dark:bg-app-bg text-app-text-secondary-light dark:text-app-text-secondary'
                  : 'bg-app-accent text-black hover:opacity-90'
              }`}
            >
              {submitting ? 'Creating...' : 'Create Dealer User'}
            </button>
          </form>
        </div>

        {/* Create kiosk user form */}
        <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-3 border border-app-border-light dark:border-app-border space-y-2">
          <div className="font-semibold text-sm">
            Create kiosk user account
          </div>
          <p className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary mb-1">
            Kiosk users are designed for public-facing kiosk devices at dealerships.
            They can create bookings and search for customers but have limited access.
          </p>

          <form onSubmit={handleCreateKioskUser} className="space-y-2">
            <div>
              <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">
                Dealership
              </label>
              <select
                className="mt-1 w-full px-3 py-2 rounded-xl bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border outline-none text-[11px]"
                value={kioskDealerId}
                onChange={(e) => setKioskDealerId(e.target.value)}
              >
                <option value="">Select a dealer</option>
                {dealers.map((d) => (
                  <option key={d._id} value={d._id}>
                    {d.name}
                    {d.address?.city && d.address?.state
                      ? ` – ${d.address.city}, ${d.address.state}`
                      : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">
                Kiosk name (e.g., "Front Desk Kiosk")
              </label>
              <input
                type="text"
                className="mt-1 w-full px-3 py-2 rounded-xl bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border outline-none text-[11px]"
                value={kioskUserName}
                onChange={(e) => setKioskUserName(e.target.value)}
                placeholder="e.g. Front Desk Kiosk"
              />
            </div>

            <div>
              <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">
                Kiosk email (for login only)
              </label>
              <input
                type="email"
                className="mt-1 w-full px-3 py-2 rounded-xl bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border outline-none text-[11px]"
                value={kioskUserEmail}
                onChange={(e) => setKioskUserEmail(e.target.value)}
                placeholder="e.g. kiosk1@dealer.com"
              />
            </div>

            <p className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">
              They will receive an email with a link to set their own password.
            </p>

            <button
              type="submit"
              disabled={kioskSubmitting}
              className={`mt-2 w-full py-3 rounded-2xl text-sm font-semibold ${
                kioskSubmitting
                  ? 'bg-app-bg-light dark:bg-app-bg text-app-text-secondary-light dark:text-app-text-secondary'
                  : 'bg-blue-500 text-white hover:opacity-90'
              }`}
            >
              {kioskSubmitting ? 'Creating...' : 'Create Kiosk User'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminDealersScreen;
